import React from 'react'
import { observer } from 'mobx-react'
import { useHistory } from 'react-router'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import {
  TableEmptyStateContainer,
  TableHeader,
  TableHost,
} from 'components/Table'
import { SearchableUser } from 'iam/models/SearchableUser'
import Workspace from 'iam/models/Workspace'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import VirtualTable from 'payouts/components/VirtualTable'
import {
  SelectionState,
  UserColumn,
  UserManagementViewModel,
} from 'user-management/views/UserManagement/UserManagementViewModel'
import { match } from 'utils/pattern-match-util'
import UserManagementTableControls from '../UserManagementTableControls'
import UserManagementTableRow from '../UserManagementTableRow'
import styles from './UserManagementTable.sass'
import UserManagementEmptyState from '../UserManagementEmptyState'

interface HeaderRowProps {
  columns: Array<UserColumn>
  selected: () => SelectionState
  toggleAll: (checked: boolean) => void
}

const HeaderRow = observer(
  ({ columns, selected, toggleAll }: HeaderRowProps) => (
    <>
      <TableHeader align="left">
        <Checkbox
          onChange={toggleAll}
          checked={match(selected(), {
            [SelectionState.AllSelected]: () => true,
            [SelectionState.NoneSelected]: () => false,
            [SelectionState.SomeSelected]: () =>
              'indeterminate' as boolean | 'indeterminate',
          })}
        />
      </TableHeader>

      {columns
        .filter((col) => col.visible)
        .map((col) => (
          <TableHeader key={col.id} canSort={col.sortable} align="left">
            {col.header}
          </TableHeader>
        ))}

      <TableHeader />
    </>
  ),
)

interface TableUserCountProps<TFilters> {
  count: number
  workspace: Workspace
  userInactiveFilter: SingleSelectFilter<keyof TFilters, boolean>
}

const formattedCount = (count: number) => {
  return Intl.NumberFormat('en-US').format(count)
}

const TableUserCount = observer(function <
  TFilters extends Record<string, boolean>,
>({ count, workspace, userInactiveFilter }: TableUserCountProps<TFilters>) {
  const isInactiveFilterEnabled = (value: boolean) =>
    userInactiveFilter.isSelected(value)
  const changeInactiveFilterTo = (value: boolean) =>
    userInactiveFilter.change(value)

  return workspace === Workspace.none ? (
    <div className={styles.tableUserCount}>
      Users
      <span data-test="users-table-count" className={styles.resultCount}>
        ({formattedCount(count ?? 0)})
      </span>
    </div>
  ) : (
    <Dropdown
      autoDismiss
      position="bottom-start"
      button={
        <Button type="text" className={styles.userStatusDropdown}>
          {userInactiveFilter.value ? 'Inactive users' : 'Active users'}
          <span data-test="users-table-count" className={styles.resultCount}>
            ({formattedCount(count ?? 0)})
          </span>
          <Icon auto name="dropdown" />
        </Button>
      }
      dataTest="user-management-active-status-dropdown"
    >
      <DropdownTitle>Users</DropdownTitle>
      <DropdownItem
        active={isInactiveFilterEnabled(false)}
        dataTest="dropdown-active-status-option"
        checkmark={isInactiveFilterEnabled(false)}
        onClick={() => changeInactiveFilterTo(false)}
      >
        Active
      </DropdownItem>
      <DropdownItem
        active={isInactiveFilterEnabled(true)}
        dataTest="dropdown-inactive-status-option"
        checkmark={isInactiveFilterEnabled(true)}
        onClick={() => changeInactiveFilterTo(true)}
      >
        Inactive
      </DropdownItem>
    </Dropdown>
  )
})

interface UserManagementTableProps {
  vm: UserManagementViewModel
}

const UserManagementTable = observer(({ vm }: UserManagementTableProps) => {
  const history = useHistory()

  const visitUserDetailsLink = (user: SearchableUser) =>
    history.push(vm.getUserDetailsLinkForUser(user))

  return (
    <div className={styles.userTable}>
      <TableUserCount
        count={vm.userSearch.totalUsers}
        workspace={vm.workspaceSelector.selectedWorkspace}
        userInactiveFilter={vm.userSearch.userInactiveFilter}
      />

      <TableHost>
        <UserManagementTableControls vm={vm} />

        {!vm.shouldShowEmptyState ? (
          <VirtualTable
            overscan={50}
            scroller={vm.userSearch.scrollProvider}
            totalCount={vm.userSearch.loadedUsersCount}
            headerContent={() => (
              <HeaderRow
                columns={vm.columns}
                selected={() => vm.selectedState}
                toggleAll={vm.toggleAll}
              />
            )}
            itemContent={(index) => {
              const user = vm.users[index]
              return (
                <UserManagementTableRow
                  key={user.publicId}
                  vm={vm}
                  user={user}
                />
              )
            }}
            onClickItemRow={visitUserDetailsLink}
          />
        ) : (
          <TableEmptyStateContainer bordered>
            <UserManagementEmptyState
              isSearchFilterApplied={vm.isSearchFilterApplied}
              onClearSearchFilterClick={() => vm.clearFilters()}
            />
          </TableEmptyStateContainer>
        )}
      </TableHost>
    </div>
  )
})

export default UserManagementTable
